<template>
    <h6>{{ label('Task') }} Assignees:</h6>
    <div class="flex gap-2">
        <div class="flex">
            <span v-if="limitedAssignedUsers?.length" class="flex">
                <custom-chip v-for="user in limitedAssignedUsers" :key="user.id" :label="user.fullname" :blueChip="true" />
            </span>
            <span
                v-if="remainingAssignedUsersCount > 0"
                class="ta-more-wrap"
                tabindex="0"
                :aria-label="`${remainingAssignedUsersCount} more ${remainingAssignedUsersCount === 1 ? 'assignee' : 'assignees'}`"
            >
                <custom-chip
                    :label="'+' + remainingAssignedUsersCount"
                    :blueChip="false"
                />
                <div class="ta-more-tooltip" role="tooltip">
                    <div class="ta-more-tooltip__title">
                        {{ remainingAssignedUsersCount }} more {{ remainingAssignedUsersCount === 1 ? 'assignee' : 'assignees' }}
                    </div>
                    <ul class="ta-more-tooltip__list">
                        <li
                            v-for="user in remainingAssignedUsers"
                            :key="user.id"
                            class="ta-more-tooltip__item"
                        >
                            <i class="pi pi-user"></i>
                            <span>{{ user.fullname }}</span>
                        </li>
                    </ul>
                    <span class="ta-more-tooltip__arrow" aria-hidden="true"></span>
                </div>
            </span>
        </div>
    </div>
    <div v-if="canDelegateTask && authStore.hasPermission(UserPermission.TASK_DELEGATE)">
        <Button
            label="Change Assignees"
            severity="help"
            size="small"
            rounded
            :loading="loadingDelegateCandidates"
            @click="openDialog"
        />
    </div>

    <Dialog
        v-model:visible="displayPopup"
        :style="{ width: '60vw' }"
        :header="`Update ${label('Task')} Assignees`"
        :modal="true"
        class="p-fluid dialog-header ta-dialog"
        @hide="resetState">

        <div class="ta-help mb-3">
            <a href="https://ignis.freshdesk.com/support/solutions/articles/202000106903-changing-a-task-assignee-adding-new-people-and-delegation"
                target="_blank" rel="noopener noreferrer">
                <i class="pi pi-question-circle mr-1"></i>How changing assignees works
            </a>
        </div>

        <section class="ta-section ta-section--current mb-3">
            <div class="ta-section__head">
                <i class="pi pi-users"></i>
                <span class="ta-section__title">Current assignees</span>
                <span class="ta-badge">{{ currentAssignees.length }}</span>
                <Button
                    :icon="showHistory ? 'pi pi-times' : 'pi pi-history'"
                    v-tooltip.top="showHistory ? 'Hide delegation history' : 'View delegation history'"
                    :aria-label="showHistory ? 'Hide history' : 'View history'"
                    :loading="historyLoading"
                    rounded
                    text
                    class="p-button-sm ta-history-toggle"
                    @click="toggleHistory"
                />
            </div>
            <div class="ta-section__body ta-section__body--flush">
                <div v-if="currentAssignees.length" class="ta-current-list">
                    <div
                        v-for="user in currentAssignees"
                        :key="user.id"
                        class="ta-current-row"
                        :class="{ 'ta-current-row--delegating': delegatingFromUser && delegatingFromUser.id === user.id }"
                    >
                        <div class="ta-current-row__info">
                            <i class="pi pi-user"></i>
                            <span class="ta-current-row__name">{{ user.fullname }}</span>
                        </div>
                        <div v-if="!delegatingFromUser" class="ta-current-row__actions">
                            <Button
                                icon="pi pi-share-alt"
                                v-tooltip.top="'Delegate this Task'"
                                severity="warning"
                                text
                                rounded
                                class="ta-current-row__btn"
                                :disabled="savingAssignee || removingUserId !== null"
                                @click="startDelegation(user)"
                            />
                            <Button
                                v-if="currentAssignees.length > 1"
                                icon="pi pi-trash"
                                v-tooltip.top="'Remove from task'"
                                severity="danger"
                                text
                                rounded
                                class="ta-current-row__btn"
                                :disabled="savingAssignee || removingUserId !== null"
                                :loading="removingUserId === user.id"
                                @click="confirmRemoveAssignee(user)"
                            />
                        </div>
                        <span v-else-if="delegatingFromUser.id === user.id" class="ta-current-row__badge">
                            delegating…
                        </span>
                    </div>
                </div>
                <div v-else class="ta-empty" style="padding: 0.75rem 0.9rem;">
                    <i class="pi pi-info-circle mr-1"></i>
                    No assignees yet. Pick someone from the list below to add.
                </div>
            </div>
        </section>

        <section v-if="delegatingFromUser" class="ta-section ta-section--delegating mb-3">
            <div class="ta-section__head">
                <i class="pi pi-share-alt"></i>
                <span class="ta-section__title">Delegation in progress</span>
                <Button
                    icon="pi pi-times"
                    label="Cancel"
                    severity="secondary"
                    text
                    size="small"
                    class="ta-history-toggle"
                    :disabled="savingAssignee"
                    @click="cancelDelegation"
                />
            </div>
            <div class="ta-section__body">
                <div class="ta-delegate-flow">
                    <div class="ta-delegate-flow__card ta-delegate-flow__card--from">
                        <div class="ta-delegate-flow__label">From</div>
                        <div class="ta-delegate-flow__user">
                            <i class="pi pi-user"></i>
                            <span>{{ delegatingFromUser.fullname }}</span>
                        </div>
                    </div>
                    <div class="ta-delegate-flow__arrow">
                        <i class="pi pi-arrow-right"></i>
                    </div>
                    <div
                        class="ta-delegate-flow__card ta-delegate-flow__card--to"
                        :class="{ 'ta-delegate-flow__card--empty': usersToAdd.length === 0 }"
                    >
                        <div class="ta-delegate-flow__label">To</div>
                        <div v-if="usersToAdd.length === 0" class="ta-delegate-flow__placeholder">
                            <i class="pi pi-arrow-down mr-1"></i>
                            Pick a target user below
                        </div>
                        <div v-else class="ta-delegate-flow__user">
                            <i class="pi pi-user"></i>
                            <span>{{ usersToAdd[0].name }} {{ usersToAdd[0].surname }}</span>
                            <Button
                                icon="pi pi-times"
                                text
                                rounded
                                size="small"
                                class="ta-delegate-flow__clear"
                                :disabled="savingAssignee"
                                @click="removeFromStaging(usersToAdd[0])"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section v-if="showHistory" class="ta-section ta-section--history mb-3">
            <div class="ta-section__head">
                <i class="pi pi-history"></i>
                <span class="ta-section__title">Delegation history</span>
                <span class="ta-badge">{{ delegationHistory.length }}</span>
            </div>
            <div class="ta-section__body">
                <div v-if="historyLoading" class="ta-empty-history">
                    <i class="pi pi-spin pi-spinner mr-1"></i>
                    Loading delegation history…
                </div>
                <div v-else-if="delegationHistory.length === 0" class="ta-empty-history">
                    <i class="pi pi-info-circle mr-1"></i>
                    No assignment records for this task.
                </div>
                <div v-else-if="!hasAnyDelegation" class="ta-empty-history">
                    <i class="pi pi-info-circle mr-1"></i>
                    This task has not been delegated yet. All users below are currently assigned; no hand-offs have happened.
                </div>

                <ol v-if="!historyLoading && delegationHistory.length > 0" class="ta-timeline">
                    <template v-for="(item, idx) in delegationHistory" :key="item.id">
                        <li class="ta-phase" :class="{ 'ta-phase--active': item.isActive, 'ta-phase--origin': idx === 0 && !item.isActive, 'ta-phase--removed': item.wasRemoved }">
                            <div class="ta-phase__step">{{ idx + 1 }}</div>
                            <div class="ta-phase__body">
                                <div class="ta-phase__header">
                                    <div class="ta-phase__left">
                                        <div class="ta-phase__user">
                                            <i :class="item.isActive ? 'pi pi-check-circle' : (item.wasRemoved ? 'pi pi-user-minus' : 'pi pi-user')"></i>
                                            {{ item.userName }}
                                        </div>
                                        <div class="ta-phase__state" :class="{ 'ta-phase__state--active': item.isActive, 'ta-phase__state--removed': item.wasRemoved }">
                                            <template v-if="item.isActive">Currently assigned</template>
                                            <template v-else-if="item.wasRemoved">Removed from this task</template>
                                            <template v-else-if="idx === 0">Initial assignee — held the task first</template>
                                            <template v-else>Received the task</template>
                                        </div>
                                        <div v-if="item.comment" class="ta-phase__comment">
                                            <i class="pi pi-comment"></i>
                                            <span>{{ item.comment }}</span>
                                        </div>
                                    </div>
                                    <div class="ta-phase__meta">
                                        <div v-if="item.createdAt" class="ta-phase__date">
                                            <i class="pi pi-calendar mr-1"></i>{{ formatDate(item.createdAt) }}
                                        </div>
                                        <Dropdown
                                            v-if="canEditStatus(item)"
                                            :model-value="item.status"
                                            :options="statusOptions"
                                            optionLabel="label"
                                            optionValue="value"
                                            :disabled="updatingStatusId === item.id"
                                            :loading="updatingStatusId === item.id"
                                            class="ta-phase__status-dropdown"
                                            @update:model-value="(val) => onStatusChange(item, val)"
                                        />
                                        <span
                                            v-else-if="!item.wasRemoved"
                                            class="ta-phase__status-badge"
                                            :class="`ta-phase__status-badge--${(item.status || 'No progress').toLowerCase().replace(/\s+/g, '-')}`"
                                        >
                                            {{ item.status || '—' }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </li>
                        <li
                            v-if="item.delegatedToName && idx < delegationHistory.length - 1"
                            class="ta-transition"
                            aria-hidden="true"
                        >
                            <div class="ta-transition__arrow"><i class="pi pi-arrow-down"></i></div>
                            <div class="ta-transition__detail">
                                <span class="ta-transition__action">
                                    <strong>{{ item.userName }}</strong> delegated the task
                                </span>
                                <span> to <strong>{{ item.delegatedToName }}</strong></span>
                                <span v-if="item.delegatedByName && item.delegatedByName !== item.userName"> (by {{ item.delegatedByName }})</span>
                                <span v-if="item.delegatedAt" class="ta-transition__date"> · {{ formatDate(item.delegatedAt) }}</span>
                            </div>
                        </li>
                        <li
                            v-else-if="item.wasRemoved && idx < delegationHistory.length - 1"
                            class="ta-transition ta-transition--removed"
                            aria-hidden="true"
                        >
                            <div class="ta-transition__arrow"><i class="pi pi-times-circle"></i></div>
                            <div class="ta-transition__detail">
                                <span class="ta-transition__action">
                                    <strong>{{ item.userName }}</strong> was removed
                                </span>
                                <span v-if="item.removedByName"> by <strong>{{ item.removedByName }}</strong></span>
                                <span v-if="item.removedAt" class="ta-transition__date"> · {{ formatDate(item.removedAt) }}</span>
                            </div>
                        </li>
                    </template>
                </ol>
            </div>
        </section>

        <section v-if="usersToAdd.length > 0" class="ta-section ta-section--pending mb-3">
            <div class="ta-section__head">
                <i :class="delegatingFromUser ? 'pi pi-briefcase' : 'pi pi-user-plus'"></i>
                <span class="ta-section__title">{{ delegatingFromUser ? 'Delegation department' : 'To add' }}</span>
                <span v-if="!delegatingFromUser" class="ta-badge ta-badge--success">{{ usersToAdd.length }}</span>
            </div>
            <div class="ta-section__body">
                <div v-if="!delegatingFromUser" class="flex flex-wrap mb-2">
                    <Chip
                        v-for="user in usersToAdd"
                        :key="user.id"
                        :label="user.name + ' ' + user.surname"
                        :removable="!savingAssignee"
                        @remove="removeFromStaging(user)"
                        class="ta-chip ta-chip--add mr-2 mb-1"
                    />
                </div>

                <div v-if="commonDepartments.length === 0" class="ta-inline-error">
                    <i class="pi pi-exclamation-triangle mr-1"></i>
                    <template v-if="delegatingFromUser">
                        Selected user has no department. Pick a different target to continue.
                    </template>
                    <template v-else>
                        Selected users do not share a department. Remove one to continue.
                    </template>
                </div>
                <div v-else class="ta-dept">
                    <base-dropdown
                        v-model="department"
                        :options="commonDepartments"
                        optionLabel="name"
                        optionValue="id"
                        label="Assign to department"
                        :filter="false"
                        :class="{ 'p-invalid': !department && savingAssignee }"
                    />
                </div>
            </div>
        </section>

        <section class="ta-section ta-section--candidates mb-2">
            <div class="ta-section__head">
                <i class="pi pi-list"></i>
                <span class="ta-section__title">
                    {{ delegatingFromUser ? 'Pick delegation target' : 'Available users' }}
                </span>
                <span class="ta-badge">{{ totalRecords }}</span>
            </div>

            <DataTable
                class="ta-candidates"
                :value="userList"
                :paginator="true"
                :lazy="true"
                :rows="5"
                ref="dt"
                dataKey="id"
                :totalRecords="totalRecords"
                responsiveLayout="scroll"
                :rowsPerPageOptions="[5, 10, 20]"
                paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                currentPageReportTemplate="{first}–{last} of {totalRecords}"
                :filters="filters"
                :globalFilterFields="['id', 'name', 'surname', 'email']"
                @page="onPage($event)"
                :loading="loadingDelegateCandidates"
            >
                <template #empty>
                    <div class="ta-candidates__empty">
                        <i class="pi pi-inbox"></i>
                        <span>No users available</span>
                    </div>
                </template>
                <template #header>
                    <div class="ta-table-header">
                        <span class="p-input-icon-left ta-search">
                            <i class="pi pi-search" />
                            <InputText
                                id="filter"
                                v-model="filters['global'].value"
                                placeholder="Search id, name, surname or email"
                                @input="onGlobalFilterInput"
                            />
                        </span>
                    </div>
                </template>
                <Column field="id" header="ID" style="width: 4rem" />
                <Column field="name" header="Name" />
                <Column field="surname" header="Surname" />
                <Column field="email" header="Email" />
                <Column header="Departments">
                    <template #body="slotProps">
                        <span
                            v-if="slotProps.data.departments?.length"
                            class="ta-candidates__departments"
                            :title="slotProps.data.departments.map(d => d.name).join(', ')"
                        >
                            {{ slotProps.data.departments.map(d => d.name).join(', ') }}
                        </span>
                        <span v-else class="ta-candidates__departments-empty">—</span>
                    </template>
                </Column>
                <Column field="actions" header="" style="width: 4rem; text-align: right">
                    <template #body="slotProps">
                        <Button
                            :icon="delegatingFromUser ? 'pi pi-check' : 'pi pi-plus'"
                            v-tooltip.left="delegatingFromUser ? 'Choose as delegation target' : 'Add as assignee'"
                            :class="delegatingFromUser ? 'p-button-rounded p-button-warning p-button-sm' : 'p-button-rounded p-button-success p-button-sm'"
                            :disabled="isCandidateUnavailable(slotProps.data.id)"
                            @click="addCandidate(slotProps.data)"
                        />
                    </template>
                </Column>
            </DataTable>
        </section>

        <section v-if="hasChanges" class="ta-section ta-section--comment mb-2">
            <div class="ta-section__head">
                <i class="pi pi-comment"></i>
                <span class="ta-section__title">Comment / reason</span>
            </div>
            <div class="ta-section__body">
                <Textarea
                    v-model="comment"
                    rows="2"
                    autoResize
                    maxlength="120"
                    :disabled="savingAssignee"
                    placeholder="Optional — share why you're making this change"
                    class="w-full"
                />
                <div class="ta-comment-counter" :class="{ 'ta-comment-counter--max': comment.length >= 120 }">
                    {{ comment.length }} / 120
                </div>
            </div>
        </section>

        <template #footer>
            <div class="ta-footer">
                <div v-if="hasChanges" class="ta-summary">
                    <i class="pi pi-info-circle"></i>
                    <span v-if="delegatingFromUser && usersToAdd.length">
                        Delegating <strong>{{ delegatingFromUser.fullname }}</strong> → <strong>{{ usersToAdd[0].name }} {{ usersToAdd[0].surname }}</strong>
                    </span>
                    <span v-else-if="usersToAdd.length">
                        <strong>{{ usersToAdd.length }}</strong> {{ usersToAdd.length === 1 ? 'user' : 'users' }} to add
                    </span>
                </div>
                <div v-else class="ta-summary ta-summary--muted">
                    <span>No pending changes</span>
                </div>
                <div class="ta-footer__actions">
                    <Button
                        label="Cancel"
                        icon="pi pi-times"
                        severity="secondary"
                        outlined
                        :disabled="savingAssignee"
                        @click="displayPopup = false"
                    />
                    <Button
                        :loading="savingAssignee"
                        :disabled="!hasChanges"
                        :label="delegatingFromUser ? 'Delegate' : 'Add assignees'"
                        :icon="delegatingFromUser ? 'pi pi-share-alt' : 'pi pi-check'"
                        :severity="delegatingFromUser ? 'warning' : 'success'"
                        @click="saveChanges"
                    />
                </div>
            </div>
        </template>
    </Dialog>
</template>

<script setup>

import { computed, defineEmits, onMounted, ref, watch } from 'vue';
import { authApi } from '@/utilities/axios';
import { authStore } from '@/store/global-stores';
import { UserPermission } from '@/modules/permission/enum';
import { FilterMatchMode } from 'primevue/api';
import {
    buildPagination,
    getOrderForHttp,
    getPage,
    getPerPage,
    setPage,
    setPerPage,
} from '@/composables/PaginationComposable';
import { showToast } from '@/utilities/toast-handler';
import { useConfirm } from 'primevue/useconfirm';
import { debounce } from 'lodash';
import { useLabels } from '@/composables/LabelComposable';

const { label } = useLabels();

const confirm = useConfirm();

const props = defineProps({
    task: {
        type: Object,
    },
    canDelegateTask: {
        type: Boolean,
    },
});

const emit = defineEmits(['taskDelegation']);

const userList = ref([]);
const totalRecords = ref(0);
const loadingDelegateCandidates = ref(true);
const displayPopup = ref(false);
const currentAssignees = ref([]);
const usersToAdd = ref([]);
const delegatingFromUser = ref(null);
const selectedTaskId = ref([]);
const savingAssignee = ref(false);
const department = ref(null);
const comment = ref('');
const showHistory = ref(false);
const historyLoading = ref(false);
const historyRows = ref([]);
const updatingStatusId = ref(null);
const removingUserId = ref(null);

const statusOptions = [
    { label: 'No progress', value: 'No progress' },
    { label: 'In progress', value: 'In progress' },
    { label: 'Completed', value: 'Completed' },
];

const filters = ref({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    id: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    surname: { value: null, matchMode: FilterMatchMode.CONTAINS },
    email: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

const company_id = computed(() => authStore.getLoggedUserCompanyId);

const remainingAssignedUsersCount = computed(() => {
    const total = props.task?.users_assigned_to?.length || 0;
    return total > 2 ? total - 2 : 0;
});

const limitedAssignedUsers = computed(() => props.task?.users_assigned_to?.slice(0, 2));

const remainingAssignedUsers = computed(() => props.task?.users_assigned_to?.slice(2) || []);

const commonDepartments = computed(() => {
    if (usersToAdd.value.length === 0) return [];
    const lists = usersToAdd.value.map(u => u.departments ?? []);
    const [first, ...rest] = lists;
    const seen = new Set();
    return first.filter(dept => {
        const key = String(dept.id);
        if (seen.has(key)) return false;
        const inAll = rest.every(list => list.some(d => String(d.id) === key));
        if (inAll) seen.add(key);
        return inAll;
    });
});

const hasChanges = computed(() => usersToAdd.value.length > 0);

const delegationHistory = computed(() => {
    const rows = historyRows.value || [];

    const resolveName = (u) => {
        if (!u) return null;
        if (typeof u === 'object') {
            const full = `${u.name || ''} ${u.surname || ''}`.trim();
            return full || (u.id != null ? `User #${u.id}` : null);
        }
        return `User #${u}`;
    };

    return rows.map(row => ({
        id: row.id,
        userName: resolveName(row.user) || 'Unknown',
        isActive: !row.delegated_to && !row.removed_at,
        wasRemoved: !!row.removed_at,
        createdAt: row.created_at,
        delegatedAt: row.delegated_at,
        delegatedToName: resolveName(row.delegated_to),
        delegatedByName: resolveName(row.delegated_by),
        removedAt: row.removed_at,
        removedByName: resolveName(row.removed_by),
        status: row.status,
        comment: row.comment,
    }));
});

const hasAnyDelegation = computed(() =>
    delegationHistory.value.some(item => !item.isActive)
);

function canEditStatus(item) {
    return item.isActive;
}

function confirmRemoveAssignee(user) {
    if (currentAssignees.value.length <= 1) return;

    confirm.require({
        header: 'Remove assignee',
        message: `Remove ${user.fullname} from this task?`,
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Remove',
        rejectLabel: 'Cancel',
        acceptClass: 'p-button-danger',
        accept: () => removeAssignee(user),
    });
}

async function removeAssignee(user) {
    removingUserId.value = user.id;
    try {
        const response = await authApi.patch(
            `${process.env.VUE_APP_API_VERSION}/tasks/delegate/${props.task.id}`,
            {
                company_id: company_id.value,
                mode: 'remove',
                comment: comment.value.trim() || null,
                users: [{ from: user.id }],
            }
        );

        const rawTask = response.data?.task;
        if (rawTask) {
            const simplifiedAssignees = {
                users_assigned_to: rawTask.users_assigned_to?.map((u) => ({
                    id: u.user.id,
                    fullname: `${u.user.name} ${u.user.surname}`,
                })) || [],
            };
            setCurrentAssignees(simplifiedAssignees);
            emit('taskDelegation', rawTask);
        }

        await loadHistory();
        comment.value = '';
    } catch (error) {
        showToast({}, extractErrorMessage(error));
    } finally {
        removingUserId.value = null;
    }
}

async function onStatusChange(item, newStatus) {
    if (!newStatus || newStatus === item.status) return;
    updatingStatusId.value = item.id;
    try {
        await authApi.patch(
            `${process.env.VUE_APP_API_VERSION}/tasks/${props.task.id}/assignment/${item.id}/status`,
            { status: newStatus, company_id: company_id.value }
        );
        await loadHistory();
    } catch (error) {
        showToast({}, extractErrorMessage(error));
    } finally {
        updatingStatusId.value = null;
    }
}

async function loadHistory() {
    if (!props.task?.id) return;
    historyLoading.value = true;
    try {
        const response = await authApi.get(
            `${process.env.VUE_APP_API_VERSION}/tasks/${props.task.id}/delegation-history`,
            { params: { company_id: company_id.value } }
        );
        historyRows.value = response.data || [];
    } catch (error) {
        showToast({ severity: 'error', summary: 'Error' }, 'Failed to load delegation history.');
        historyRows.value = [];
    } finally {
        historyLoading.value = false;
    }
}

function toggleHistory() {
    showHistory.value = !showHistory.value;
    if (showHistory.value && historyRows.value.length === 0) {
        loadHistory();
    }
}

function formatDate(value) {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
}

watch(commonDepartments, (depts) => {
    if (depts.length === 1) {
        department.value = depts[0].id;
    } else if (!depts.some(d => String(d.id) === String(department.value))) {
        department.value = null;
    }
});

const handleTaskChange = (task) => {
    if (task && task.id) {
        selectedTaskId.value = task.id;
        setCurrentAssignees(task);
        buildPagination("taskCandidates", 0, 10, [{ field: "id", order: -1 }]);
        loadTaskCandidate(task.id);
    }
};

onMounted(() => {
    handleTaskChange(props.task);
});

watch(
    () => props.task,
    (task) => {
        handleTaskChange(task);
    }
);

function setCurrentAssignees(task) {
    if (task) {
        currentAssignees.value = [...(task.users_assigned_to || [])];
    }
}

function openDialog() {
    resetState();
    displayPopup.value = true;
}

function resetState() {
    usersToAdd.value = [];
    delegatingFromUser.value = null;
    department.value = null;
    comment.value = '';
    setCurrentAssignees(props.task);
}

function startDelegation(user) {
    delegatingFromUser.value = user;
    usersToAdd.value = [];
    department.value = null;
}

function cancelDelegation() {
    delegatingFromUser.value = null;
    usersToAdd.value = [];
    department.value = null;
}

function addCandidate(userData) {
    if (delegatingFromUser.value) {
        if (userData.id === delegatingFromUser.value.id) return;
        usersToAdd.value = [userData];
        return;
    }

    const userDepartments = userData.departments ?? [];

    if (!userDepartments.length) {
        showToast(
            { severity: "warn", summary: "User not assignable" },
            `${userData.name} does not belong to any department.`
        );
        return;
    }

    if (isCandidateUnavailable(userData.id)) return;

    usersToAdd.value.push(userData);
}

function removeFromStaging(userData) {
    usersToAdd.value = usersToAdd.value.filter(u => u.id !== userData.id);
}

function isCandidateUnavailable(userId) {
    if (delegatingFromUser.value) {
        return userId === delegatingFromUser.value.id
            || usersToAdd.value.some(u => u.id === userId);
    }
    return usersToAdd.value.some(u => u.id === userId)
        || currentAssignees.value.some(u => u.id === userId);
}

const onPage = (event) => {
    setPage('taskCandidates', event.page);
    setPerPage('taskCandidates', event.rows);
    loadTaskCandidate(selectedTaskId.value);
};

const debouncedLoadUserList = debounce(() => {
    loadTaskCandidate(selectedTaskId.value);
}, 300);

const onGlobalFilterInput = () => {
    debouncedLoadUserList();
};

function loadTaskCandidate(taskId) {
    const page_size = getPerPage('taskCandidates');
    const page = getPage('taskCandidates');
    const sortOrder = getOrderForHttp('taskCandidates');

    loadingDelegateCandidates.value = true;
    authApi.get(`${process.env.VUE_APP_API_VERSION}/tasks/delegate/${taskId}/candidates`, {
        params: {
            company_id: company_id.value,
            page_size,
            page: page + 1,
            sort: sortOrder,
            search: filters?.value?.global?.value || '',
        },
    })
    .then((response) => {
        if (response.statusCode === 200) {
            userList.value = response.data.data;
            totalRecords.value = response.data.total ?? response.data.data?.length ?? 0;
        }
    })
    .catch(() => {
        showToast({ severity: 'error', summary: 'Error' }, 'Failed to load candidate users.');
    })
    .finally(() => {
        loadingDelegateCandidates.value = false;
    });
}

function extractErrorMessage(error) {
    if (error?.status === 400) {
        const res = error.data;
        const validationErrors = res?.data;
        if (validationErrors && typeof validationErrors === 'object') {
            const allMessages = Object.values(validationErrors).flat();
            if (allMessages.length) return allMessages[0];
        }
        return res?.message || 'Validation error';
    }
    return error?.data?.message || error?.message || 'An unexpected error occurred.';
}

async function saveChanges() {
    if (!hasChanges.value) return;

    const url = `${process.env.VUE_APP_API_VERSION}/tasks/delegate/${props.task.id}`;

    const applyResponseToState = (rawTask) => {
        const simplifiedAssignees = {
            users_assigned_to: rawTask.users_assigned_to?.map((userObj) => ({
                id: userObj.user.id,
                fullname: `${userObj.user.name} ${userObj.user.surname}`,
            })) || [],
        };
        setCurrentAssignees(simplifiedAssignees);
        emit('taskDelegation', rawTask);
    };

    if (delegatingFromUser.value) {
        if (commonDepartments.value.length === 0) {
            showToast(
                { severity: "warn", summary: "No department" },
                'Selected user has no department. Pick a different target.'
            );
            return;
        }
        if (!department.value) {
            showToast(
                { severity: "warn", summary: 'Missing Department' },
                'Please select a department before delegating.'
            );
            return;
        }

        savingAssignee.value = true;
        try {
            const response = await authApi.patch(url, {
                company_id: company_id.value,
                to_department_id: department.value,
                comment: comment.value.trim() || 'Task delegated to a different user',
                users: [{
                    from: delegatingFromUser.value.id,
                    to: [usersToAdd.value[0].id],
                }],
            });
            applyResponseToState(response.data.task);

            await loadHistory();

            delegatingFromUser.value = null;
            usersToAdd.value = [];
            comment.value = '';
            displayPopup.value = false;
        } catch (error) {
            showToast({}, extractErrorMessage(error));
        } finally {
            savingAssignee.value = false;
        }
        return;
    }

    if (commonDepartments.value.length === 0) {
        showToast(
            { severity: "warn", summary: "No shared department" },
            'Selected users do not share a department.'
        );
        return;
    }
    if (!department.value) {
        showToast(
            { severity: "warn", summary: 'Missing Department' },
            'Please select a department before submitting.'
        );
        return;
    }

    savingAssignee.value = true;

    const fromUserId = (props.task?.users_assigned_to || [])[0]?.id;

    try {
        const response = await authApi.patch(url, {
            company_id: company_id.value,
            to_department_id: department.value,
            mode: 'add',
            comment: comment.value.trim() || 'Task assignees updated',
            users: [{ from: fromUserId, to: usersToAdd.value.map(u => u.id) }],
        });
        applyResponseToState(response.data.task);

        await loadHistory();

        usersToAdd.value = [];
        comment.value = '';
        displayPopup.value = false;
    } catch (error) {
        showToast({}, extractErrorMessage(error));
    } finally {
        savingAssignee.value = false;
    }
}

</script>

<style lang="scss" src="@/assets/sass/components/task-ui.scss"></style>
